import * as Sentry from '@sentry/react'

// Public client key — safe to commit.
const DSN = 'https://c3fc5e4f266de3d76ad7204391bc76ee@o4511728872456192.ingest.us.sentry.io/4511729830854657'

// Only the production host reports. Every new Sentry issue auto-creates a
// Perch ticket and dispatches a fix agent, so staging (dub-dev.onrender.com)
// and local dev must stay silent.
const PRODUCTION_HOSTNAME = 'app.getdub.com'

// Errors that aren't ours and never affect the app, but land in Sentry as a
// "crash" and auto-file a ticket each time. Sentry tests these against the
// event message and every exception value, so a partial match is enough.
// Exported so the patterns can be unit-tested against the exact strings Sentry
// reported.
export const IGNORED_ERROR_PATTERNS = [
  // Third-party in-app browsers / native wrappers we don't control. Some of
  // them inject a `sendDataToNative` bridge that dereferences the iOS-only
  // `window.webkit.messageHandlers`, which throws a TypeError everywhere else.
  // Message wording varies by browser (WebKit vs V8), so match on the stable
  // substrings.
  /webkit\.messageHandlers/i,
  /sendDataToNative/i,
  // Microsoft's Office/Outlook browser extensions inject a content script whose
  // internal message bus rejects a promise with this plain string when the
  // object it was updating has already been torn down. It surfaces as an
  // unhandled rejection with no stack trace and no user, from whatever page
  // the extension's user happened to be on (ticket 4ac8a72a, DUB-WEB-3). The
  // id and method vary between reports, so match the stable shape.
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // iOS apps that open links in their own in-app browser (a WKWebView) inject
  // a script that talks to the host app over postMessage. When the host never
  // answers, WebKit throws this from that injected script. Our code never
  // calls postMessage, and the native dub app doesn't load from the production
  // host, so it's always someone else's wrapper (ticket 1b27f53b).
  /WKWebView API client did not respond to this postMessage/,
  // A script on the page (a Safari extension, in the one report we have)
  // dispatched a hand-built `unhandledrejection` CustomEvent at window. Sentry's
  // global handler can't tell it from a real one, so it files the event object
  // itself as the "rejection" — isTrusted is false, there's no stack, and the
  // reason is empty. It arrived together with the empty-object variant below,
  // in the same second from the same session (tickets 6d227638, 5f0ed942).
  // Nothing in our code dispatches CustomEvents or rejects with a bare `{}`,
  // and a keyless rejection carries nothing to debug even if one did.
  /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/,
  /^Object captured as promise rejection with keys: \[object has no keys\]$/,
]

export function initSentry() {
  if (window.location.hostname !== PRODUCTION_HOSTNAME) return

  Sentry.init({
    dsn: DSN,
    environment: 'production',
    // __COMMIT_SHA__ is defined by Vite at build time; guard for test runners
    // (vitest) that don't apply the define.
    release: typeof __COMMIT_SHA__ !== 'undefined' && __COMMIT_SHA__ ? __COMMIT_SHA__ : undefined,
    // Error monitoring only — no tracing, no replay.
    tracesSampleRate: 0,
    ignoreErrors: IGNORED_ERROR_PATTERNS,
    beforeSend(event) {
      if (isInjectedNativeBridgeError(event)) return null
      return event
    },
  })
}

// True when the event's exception originated in an injected `sendDataToNative`
// native-bridge shim (see initSentry above). Checks stack frame function names
// as a backstop for when the error message doesn't mention the culprit.
export function isInjectedNativeBridgeError(event) {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false
  return values.some((value) => {
    if (typeof value?.value === 'string' && /webkit\.messageHandlers|sendDataToNative/i.test(value.value)) {
      return true
    }
    const frames = value?.stacktrace?.frames
    return Array.isArray(frames) && frames.some((frame) => /sendDataToNative/i.test(frame?.function || ''))
  })
}

export function captureException(error, extra) {
  Sentry.captureException(error, extra ? { extra } : undefined)
}
